import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ActivityKind, PersonEvent } from "../../zodios/apiDefinitions/api/staffUserActivity";
import type { FeatureId, PersonStatus } from "./model";

export function useActivityLabels() {
  const { t } = useTranslation(["user_activity"]);

  return useMemo(() => {
    const kind = (value: ActivityKind): string => {
      switch (value) {
        case "page_view":
          return t(($) => $.user_activity["Pages viewed"]);
        case "campaign_change":
          return t(($) => $.user_activity["Campaign changes"]);
        case "keyword_change":
          return t(($) => $.user_activity["Keyword & target changes"]);
        case "rule_change":
          return t(($) => $.user_activity["Rule changes"]);
        case "export":
          return t(($) => $.user_activity["Reports downloaded"]);
        case "saved_view":
          return t(($) => $.user_activity["Views & dashboards saved"]);
        case "insight":
          return t(($) => $.user_activity["Insights reviewed"]);
        case "amc_report":
          return t(($) => $.user_activity["AMC reports run"]);
        case "creative":
          return t(($) => $.user_activity["AI images created"]);
      }
    };

    const feature = (value: FeatureId): string => {
      switch (value) {
        case "dashboard":
          return t(($) => $.user_activity["Dashboard"]);
        case "campaigns":
          return t(($) => $.user_activity["Campaigns"]);
        case "keywords":
          return t(($) => $.user_activity["Keywords & targets"]);
        case "rules":
          return t(($) => $.user_activity["Rules & automation"]);
        case "products":
          return t(($) => $.user_activity["Products"]);
        case "categories":
          return t(($) => $.user_activity["Categories"]);
        case "pricing":
          return t(($) => $.user_activity["Pricing"]);
        case "reports":
          return t(($) => $.user_activity["Reports & AMC"]);
        case "saved_views":
          return t(($) => $.user_activity["Saved views & dashboards"]);
        case "insights":
          return t(($) => $.user_activity["Insights"]);
        case "creative":
          return t(($) => $.user_activity["AI creative"]);
      }
    };

    const status = (value: PersonStatus): string => {
      switch (value) {
        case "active":
          return t(($) => $.user_activity["Active"]);
        case "slowing":
          return t(($) => $.user_activity["Slowing down"]);
        case "inactive":
          return t(($) => $.user_activity["Inactive"]);
        case "never":
          return t(($) => $.user_activity["Never logged in"]);
        case "invited":
          return t(($) => $.user_activity["Invite pending"]);
      }
    };

    const statusHint = (value: PersonStatus): string => {
      switch (value) {
        case "active":
          return t(($) => $.user_activity["Used Trellis in the last 7 days"]);
        case "slowing":
          return t(($) => $.user_activity["Last seen 8 to 30 days ago"]);
        case "inactive":
          return t(($) => $.user_activity["Not seen for more than 30 days"]);
        case "never":
          return t(($) => $.user_activity["Has access but has never logged in"]);
        case "invited":
          return t(($) => $.user_activity["Was invited but hasn't accepted yet"]);
      }
    };

    const event = ({ kind: eventKind, detail, action, label, ref_id }: PersonEvent): string => {
      const name = label ?? (ref_id !== null ? `#${ref_id}` : "");
      const created = action === "created";
      switch (eventKind) {
        case "page_view":
          if (detail === "dashboard") return t(($) => $.user_activity["Opened the dashboard"]);
          if (detail === "ads") return t(($) => $.user_activity["Viewed campaign “{name}”"], { name });
          if (detail === "product") return t(($) => $.user_activity["Viewed product “{name}”"], { name });
          if (detail === "category") return t(($) => $.user_activity["Viewed category “{name}”"], { name });
          return t(($) => $.user_activity["Viewed pricing plan “{name}”"], { name });
        case "campaign_change":
          if (detail === "campaign") {
            return created ?
                t(($) => $.user_activity["Created campaign “{name}”"], { name })
              : t(($) => $.user_activity["Changed campaign “{name}”"], { name });
          }
          if (detail === "product_ad") {
            return created ?
                t(($) => $.user_activity["Added a product to a campaign"])
              : t(($) => $.user_activity["Changed a product ad"]);
          }
          if (detail === "account_controls")
            return t(($) => $.user_activity["Changed account-wide bid & budget controls"]);
          return t(($) => $.user_activity["Changed campaign settings"]);
        case "keyword_change":
          if (detail === "target") {
            return created ?
                t(($) => $.user_activity["Added target “{name}”"], { name })
              : t(($) => $.user_activity["Changed target “{name}”"], { name });
          }
          if (detail === "walmart_item") return t(($) => $.user_activity["Changed a Walmart ad item"]);
          return created ?
              t(($) => $.user_activity["Added keyword “{name}”"], { name })
            : t(($) => $.user_activity["Changed keyword “{name}”"], { name });
        case "rule_change":
          return t(($) => $.user_activity["Edited rule “{name}”"], { name });
        case "export":
          return detail === "scheduled_export" ?
              t(($) => $.user_activity["Scheduled a recurring report “{name}”"], { name })
            : t(($) => $.user_activity["Downloaded report “{name}”"], { name });
        case "saved_view":
          return detail === "dashboard" ?
              t(($) => $.user_activity["Created dashboard “{name}”"], { name })
            : t(($) => $.user_activity["Saved view “{name}”"], { name });
        case "insight":
          return detail === "insight_dismissed" ?
              t(($) => $.user_activity["Dismissed insight “{name}”"], { name })
            : t(($) => $.user_activity["Read insight “{name}”"], { name });
        case "amc_report":
          return t(($) => $.user_activity["Ran AMC report “{name}”"], { name });
        case "creative":
          return t(($) => $.user_activity["Generated AI product images"]);
      }
    };

    return { kind, feature, status, statusHint, event };
  }, [t]);
}
